<template>
  <div class="coupon-form">
    <a-form-model
      ref="formModel"
      :model="form"
      :label-col="labelCol"
      :wrapper-col="wrapperCol"
    >
      <div class="section-title">优惠券基本信息</div>

      <a-form-model-item label="标题">
        <a-input
          v-model="form.title"
          :disabled="disabled"
          placeholder="请输入"
          class="field-md"
        />
      </a-form-model-item>

      <a-form-model-item label="券类型">
        <a-select
          v-model="form.couponType"
          :disabled="disabled"
          placeholder="请选择"
          class="field-sm"
        >
          <a-select-option
            v-for="item in couponTypeOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item v-if="form.couponType === 2" label="折扣率">
        <a-input
          v-model="form.discountRate"
          :disabled="disabled"
          placeholder="请输入0.1-9.9"
          class="field-sm"
          addon-after="折"
        />
      </a-form-model-item>

      <a-form-model-item v-if="form.couponType !== 2" label="面额">
        <a-input
          v-model="form.faceAmount"
          :disabled="disabled"
          placeholder="请输入"
          class="field-sm"
          addon-after="元"
        />
      </a-form-model-item>

      <a-form-model-item label="数量">
        <a-input
          v-model="form.quantity"
          :disabled="disabled"
          placeholder="-1为不限数量"
          class="field-sm"
          addon-after="份"
        />
      </a-form-model-item>

      <a-form-model-item label="核销门槛">
        <span class="inline-text">满</span>
        <a-input
          v-model="form.useThreshold"
          :disabled="disabled"
          placeholder="请输入"
          class="field-xs"
          addon-after="元"
        />
        <span class="inline-text">可用</span>
      </a-form-model-item>

      <a-form-model-item v-if="form.couponType === 2" label="最高抵扣">
        <a-input
          v-model="form.maxDeduction"
          :disabled="disabled"
          placeholder="请输入"
          class="field-sm"
          addon-after="元"
        />
      </a-form-model-item>

      <a-form-model-item label="可用类目">
        <a-checkbox-group v-model="form.availableCategory" :disabled="disabled">
          <a-checkbox
            v-for="item in productTypeList"
            :key="item.code"
            :value="item.code"
          >
            {{ item.name }}
          </a-checkbox>
        </a-checkbox-group>
        <a-checkbox
          class="category-check-all"
          :disabled="disabled"
          :indeterminate="categoryIndeterminate"
          :checked="categoryCheckAll"
          @change="onCategoryCheckAllChange"
        >
          全选
        </a-checkbox>
      </a-form-model-item>

      <a-form-model-item label="可用商品">
        <div class="pay-after-row">
          <a-select
            v-model="form.availableGoods"
            :disabled="disabled"
            placeholder="请选择"
            class="field-sm pay-after-select"
            @change="onAvailableGoodsChange"
          >
            <a-select-option :value="1">全部商品</a-select-option>
            <a-select-option :value="2">指定商品</a-select-option>
          </a-select>
          <template v-if="form.availableGoods === 2">
            <a-select
              v-model="form.availableGoodsTargetType"
              :disabled="disabled"
              class="field-sm pay-after-select"
              @change="clearAvailableGoodsTarget"
            >
              <a-select-option :value="1">指定单个商品</a-select-option>
              <a-select-option :value="2">指定商品分组</a-select-option>
            </a-select>
            <a-button
              v-if="!disabled"
              class="pay-after-select-btn"
              @click="openSelectDialog"
            >
              选择
            </a-button>
            <span v-if="form.availableGoodsName" class="selected-name">{{ form.availableGoodsName }}</span>
          </template>
        </div>
      </a-form-model-item>

      <div class="section-title">派券核销信息</div>

      <a-form-model-item label="派券方式">
        <a-radio-group v-model="form.receiveMethod" :disabled="disabled">
          <a-radio
            v-for="item in distributeWayOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </a-radio>
        </a-radio-group>
      </a-form-model-item>

      <a-form-model-item label="可领券时间">
        <a-range-picker
          v-model="form.receiveTimeRange"
          show-time
          :disabled="disabled"
          format="YYYY-MM-DD HH:mm"
          class="field-lg"
        />
      </a-form-model-item>

      <a-form-model-item label="有效期">
        <a-select
          v-model="form.validityType"
          :disabled="disabled"
          class="field-sm"
          style="margin-right: 12px"
        >
          <a-select-option :value="1">固定有效期</a-select-option>
          <a-select-option :value="2">限定有效期</a-select-option>
        </a-select>
        <template v-if="form.validityType === 1">
          <span class="inline-text">有效期截止至：</span>
          <a-date-picker
            v-model="form.validityEnd"
            show-time
            :disabled="disabled"
            format="YYYY-MM-DD HH:mm"
          />
        </template>
        <template v-else>
          <span class="inline-text">领取后</span>
          <a-input
            v-model="form.validityDays"
            :disabled="disabled"
            class="field-xs"
            addon-after="天"
          />
          <span class="inline-text">内可用</span>
        </template>
      </a-form-model-item>

      <a-form-model-item label="分销是否可用">
        <a-radio-group v-model="form.distributionAvailable" :disabled="disabled">
          <a-radio :value="1">可用</a-radio>
          <a-radio :value="0">不可用</a-radio>
        </a-radio-group>
        <span v-if="form.distributionAvailable === 0" class="field-tip">
          （分销员店铺下访问不可领取、核销）
        </span>
      </a-form-model-item>

      <div class="section-title">使用规则说明</div>

      <a-form-model-item label="使用规则">
        <a-textarea
          v-model="form.useRule"
          :disabled="disabled"
          placeholder="请输入"
          :rows="5"
          class="field-lg"
        />
      </a-form-model-item>

      <a-form-model-item :wrapper-col="offsetCol" class="footer-actions">
        <slot name="footer" />
      </a-form-model-item>
    </a-form-model>

    <template v-if="!disabled">
      <selected-product-dialog
        ref="productDialog"
        :show="showSelectedDialog"
        :width="1200"
        :itemNames="['search_proId', 'search_proName']"
        :query-params="selectedQueryParams"
        :requestPromise="getModularProductPromise"
        row-key="proId"
        :columns="getGoodsColumns(true)"
        :custom-pagination="true"
        :rowSelection="productRowSelection"
        @close="showSelectedDialog = false"
      >
        <a-form-item label="商品ID" slot="search_proId" v-bind="currentDialogLayout">
          <a-input v-model="selectedQueryParams.proId" style="width: 170px" placeholder="请输入商品ID" />
        </a-form-item>
        <a-form-item label="商品名称" slot="search_proName" v-bind="currentDialogLayout">
          <a-input v-model="selectedQueryParams.proName" style="width: 170px" placeholder="请输入商品名称" />
        </a-form-item>
        <template slot="proCatalog" slot-scope="rowItem">
          {{ getProCatalogName(rowItem.record.proCatalog, proCatalogEnum) }}
        </template>
        <div class="coupon-select-footer-btns" slot="footer">
          <a-space>
            <a-button @click="showSelectedDialog = false">取消</a-button>
            <a-button type="primary" @click="confirmProduct">确定</a-button>
          </a-space>
        </div>
      </selected-product-dialog>

      <selected-product-dialog
        ref="groupDialog"
        :show="showSelectedGroupDialog"
        :width="1200"
        :col="2"
        title="选择商品分组"
        :itemNames="['search_groupName']"
        :query-params="selectedQueryGroupParams"
        :request-promise="getGroupRequest"
        :columns="groupSelectColumns"
        row-key="groupId"
        :custom-pagination="true"
        :rowSelection="groupRowSelection"
        @close="showSelectedGroupDialog = false"
      >
        <a-form-item label="商品分组名称" slot="search_groupName" v-bind="currentDialogLayout">
          <a-input
            v-model="selectedQueryGroupParams.groupName"
            style="width: 100%"
            placeholder="请输入商品分组名称"
          />
        </a-form-item>
        <div class="coupon-select-footer-btns" slot="footer">
          <a-space>
            <a-button @click="showSelectedGroupDialog = false">取消</a-button>
            <a-button type="primary" @click="confirmGroup">确定</a-button>
          </a-space>
        </div>
      </selected-product-dialog>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import SelectedProductDialog from '@/customComponents/SelectedProductDialog/SelectedProductDialog.vue'
import productMixins from '@/utils/mixins/productMixins'
import { COUPON_TYPE, DISTRIBUTE_WAY, toSelectOptions } from '../constants/couponConstants'

export function createDefaultCouponForm () {
  return {
    title: '',
    couponType: 1,
    faceAmount: '',
    discountRate: '',
    quantity: '',
    useThreshold: '',
    maxDeduction: '',
    availableCategory: [],
    availableGoods: 1,
    availableGoodsTargetType: 1,
    availableGoodsId: '',
    availableGoodsName: '',
    receiveMethod: 1,
    receiveTimeRange: [],
    validityType: 1,
    validityEnd: null,
    validityDays: '',
    distributionAvailable: 0,
    useRule: ''
  }
}

function toMoment (value) {
  if (!value) return null
  const m = moment(value)
  return m.isValid() ? m : null
}

export function mapDetailToCouponForm (data = {}) {
  const start = toMoment(data.receiveStartTime)
  const end = toMoment(data.receiveEndTime)
  return {
    title: data.couponName || '',
    couponType: data.couponType,
    faceAmount: data.couponAmt,
    discountRate: data.discountRate,
    quantity: data.stockNum,
    useThreshold: data.useThreshold,
    maxDeduction: data.maxDeductionAmount,
    availableCategory: data.categoryTypeList || [],
    availableGoods: data.useScopeType,
    availableGoodsTargetType: data.productScopeType,
    availableGoodsId: data.productScopeId,
    availableGoodsName: data.productScopeName || '',
    receiveMethod: data.distributeWay,
    receiveTimeRange: start && end ? [start, end] : [],
    validityType: data.validType,
    validityEnd: toMoment(data.validEndTime),
    validityDays: data.validDays,
    distributionAvailable: data.distributorEnabled,
    useRule: data.useRule || ''
  }
}

export default {
  name: 'CouponForm',
  components: {
    SelectedProductDialog
  },
  mixins: [productMixins],
  props: {
    value: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      labelCol: { span: 3 },
      wrapperCol: { span: 18 },
      offsetCol: { offset: 3, span: 18 },
      productTypeList: this.getAppConstants().getProductTypeList(),
      showSelectedDialog: false,
      showSelectedGroupDialog: false,
      selectedRowKeys: [],
      selectedRows: [],
      selectedGroupRowKeys: [],
      selectedGroupRows: [],
      selectedQueryParams: {
        proName: '',
        proId: '',
        proCatalogs: ''
      },
      selectedQueryGroupParams: {
        groupName: '',
        groupStatus: 1
      }
    }
  },
  computed: {
    form () {
      return this.value
    },
    couponTypeOptions () {
      return toSelectOptions(COUPON_TYPE)
    },
    // 暂未开放：分享页领取(2)、支付后领取(4) — 新建/详情均不展示
    distributeWayOptions () {
      return toSelectOptions(DISTRIBUTE_WAY).filter(
        item => item.value === 1 || item.value === 3 || item.value === 5
      )
    },
    groupSelectColumns () {
      return this.getGroupColumns().filter(col => col.dataIndex !== 'action')
    },
    productRowSelection () {
      return {
        type: 'radio',
        selectedRowKeys: this.selectedRowKeys,
        onChange: (keys, rows) => {
          this.selectedRowKeys = keys
          this.selectedRows = rows
        }
      }
    },
    groupRowSelection () {
      return {
        type: 'radio',
        selectedRowKeys: this.selectedGroupRowKeys,
        onChange: (keys, rows) => {
          this.selectedGroupRowKeys = keys
          this.selectedGroupRows = rows
        }
      }
    },
    categoryCheckAll () {
      const selected = this.form.availableCategory || []
      return this.productTypeList.length > 0 && selected.length === this.productTypeList.length
    },
    categoryIndeterminate () {
      const selected = this.form.availableCategory || []
      return selected.length > 0 && selected.length < this.productTypeList.length
    }
  },
  methods: {
    onCategoryCheckAllChange (e) {
      this.form.availableCategory = e.target.checked
        ? this.productTypeList.map(item => item.code)
        : []
    },
    syncProductQueryCatalog () {
      const selectedCategories = this.form.availableCategory || []
      this.selectedQueryParams.proCatalogs = selectedCategories.length ? [...selectedCategories] : ''
    },
    normalizeKey (key) {
      if (key === '' || key == null) return key
      return /^\d+$/.test(String(key)) ? Number(key) : key
    },
    clearAvailableGoodsTarget () {
      this.form.availableGoodsId = ''
      this.form.availableGoodsName = ''
    },
    onAvailableGoodsChange () {
      if (this.disabled) return
      this.form.availableGoodsTargetType = 1
      this.clearAvailableGoodsTarget()
    },
    syncDialogSelect (refName, keys, rows) {
      this.$nextTick(() => {
        const dialog = this.$refs[refName]
        if (dialog && dialog.$refs.pageList) {
          dialog.updateSelect(keys, rows)
        }
      })
    },
    openSelectDialog () {
      const isGroup = this.form.availableGoodsTargetType === 2
      const key = this.normalizeKey(this.form.availableGoodsId)
      const name = this.form.availableGoodsName

      if (isGroup) {
        this.selectedGroupRowKeys = key !== '' && key != null ? [key] : []
        this.selectedGroupRows = this.selectedGroupRowKeys.length
          ? [{ groupId: key, groupName: name }]
          : []
        this.showSelectedGroupDialog = true
        this.syncDialogSelect('groupDialog', this.selectedGroupRowKeys, this.selectedGroupRows)
        return
      }

      this.selectedRowKeys = key !== '' && key != null ? [key] : []
      this.selectedRows = this.selectedRowKeys.length
        ? [{ proId: key, proName: name }]
        : []
      this.syncProductQueryCatalog()
      this.showSelectedDialog = true
      this.syncDialogSelect('productDialog', this.selectedRowKeys, this.selectedRows)
    },
    confirmProduct () {
      if (!this.selectedRows.length) {
        this.$message.error('请选择产品')
        return
      }
      const { proId, proName } = this.selectedRows[0]
      this.form.availableGoodsId = proId
      this.form.availableGoodsName = proName
      this.showSelectedDialog = false
    },
    confirmGroup () {
      if (!this.selectedGroupRows.length) {
        this.$message.error('请选择商品分组')
        return
      }
      const { groupId, groupName } = this.selectedGroupRows[0]
      this.form.availableGoodsId = groupId
      this.form.availableGoodsName = groupName
      this.showSelectedGroupDialog = false
    },
    toNumber (value, fallback = 0) {
      if (value === '' || value == null) return fallback
      const num = Number(value)
      return Number.isNaN(num) ? fallback : num
    },
    formatDateTime (value) {
      if (!value) return ''
      return moment(value).format('YYYY-MM-DD HH:mm:ss')
    },
    validate () {
      const form = this.form
      if (!form.title) {
        this.$message.error('请输入标题')
        return false
      }
      if (form.couponType === 2) {
        const rate = this.toNumber(form.discountRate, NaN)
        if (Number.isNaN(rate) || rate < 0.1 || rate > 9.9) {
          this.$message.error('请输入0.1-9.9的折扣率')
          return false
        }
        if (form.maxDeduction === '' || form.maxDeduction == null) {
          this.$message.error('请输入最高抵扣')
          return false
        }
      } else if (form.faceAmount === '' || form.faceAmount == null) {
        this.$message.error('请输入面额')
        return false
      }
      if (form.quantity === '' || form.quantity == null) {
        this.$message.error('请输入数量')
        return false
      }
      if (form.useThreshold === '' || form.useThreshold == null) {
        this.$message.error('请输入核销门槛')
        return false
      }
      if (!form.availableCategory || !form.availableCategory.length) {
        this.$message.error('请选择可用类目')
        return false
      }
      if (form.availableGoods === 2 && !form.availableGoodsId) {
        this.$message.error('请选择指定商品或分组')
        return false
      }
      if (!form.receiveTimeRange || form.receiveTimeRange.length !== 2) {
        this.$message.error('请选择可领券时间')
        return false
      }
      if (form.validityType === 1 && !form.validityEnd) {
        this.$message.error('请选择有效期截止时间')
        return false
      }
      if (form.validityType === 2 && (form.validityDays === '' || form.validityDays == null)) {
        this.$message.error('请输入领取后可用天数')
        return false
      }
      return true
    },
    buildCreateParams () {
      const form = this.form
      const isDiscount = form.couponType === 2
      const isSpecified = form.availableGoods === 2
      const params = {
        couponName: form.title,
        couponType: form.couponType,
        discountRate: isDiscount ? this.toNumber(form.discountRate) : 0,
        stockNum: this.toNumber(form.quantity),
        useThreshold: this.toNumber(form.useThreshold),
        maxDeductionAmount: isDiscount ? this.toNumber(form.maxDeduction) : 0,
        categoryTypeList: form.availableCategory,
        useScopeType: form.availableGoods,
        productScopeType: isSpecified ? form.availableGoodsTargetType : '',
        productScopeId: isSpecified ? this.toNumber(form.availableGoodsId) : '',
        productScopeName: isSpecified ? (form.availableGoodsName || '') : '',
        distributeWay: form.receiveMethod,
        receiveStartTime: this.formatDateTime(form.receiveTimeRange[0]),
        receiveEndTime: this.formatDateTime(form.receiveTimeRange[1]),
        validType: form.validityType,
        validEndTime: form.validityType === 1 ? this.formatDateTime(form.validityEnd) : '',
        validDays: form.validityType === 2 ? this.toNumber(form.validityDays) : 0,
        distributorEnabled: form.distributionAvailable,
        useRule: form.useRule || ''
      }
      if (!isDiscount) {
        params.couponAmt = this.toNumber(form.faceAmount)
      }
      return params
    }
  }
}
</script>

<style scoped lang="less">
.coupon-form {
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin: 8px 0 20px;
  }

  .field-xs {
    width: 180px;
    margin: 0 8px;
  }

  .field-sm {
    max-width: 280px;
  }

  .field-md {
    max-width: 480px;
  }

  .field-lg {
    max-width: 560px;
  }

  .category-check-all {
    margin-left: 8px;
  }

  .inline-text {
    color: rgba(0, 0, 0, 0.65);
  }

  .field-tip {
    margin-left: 8px;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .selected-name {
    margin-right: 12px;
    color: #faad14;
  }

  .pay-after-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 12px;
  }

  .pay-after-select {
    width: 160px;
    margin-right: 12px;
  }

  .pay-after-select-btn {
    margin-right: 12px;
  }

  .footer-actions {
    margin-top: 24px;

    /deep/ .ant-form-item-children {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }
  }
}

.coupon-select-footer-btns {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
